const PERIOD_DAY_MS = 24 * 60 * 60 * 1000;

const formatTradeTimestampShort = (timestamp) => {
  if (!timestamp) {
    return 'Unknown';
  }

  const normalized = timestamp.includes('T') ? timestamp : timestamp.replace(' ', 'T');
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return timestamp;
  }

  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const buildTradeLabels = (data = []) => {
  const labels = data.map((item) => formatTradeTimestampShort(item.timestamp));
  const labelCounts = labels.reduce((acc, label) => {
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});

  return data.map((item, index) => {
    const label = labels[index];
    if (labelCounts[label] === 1) {
      return label;
    }

    return `Trade ${item.trade_number}`;
  });
};

const startOfDay = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const endOfDay = (date) => {
  const normalized = new Date(date);
  normalized.setHours(23, 59, 59, 0);
  return normalized;
};

export const formatDateTimeForApi = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const calculateDateRange = (period, now = new Date()) => {
  if (period === 'DAY') {
    return {
      startDate: formatDateTimeForApi(new Date(now.getTime() - PERIOD_DAY_MS)),
      endDate: formatDateTimeForApi(now),
    };
  }

  const end = endOfDay(now);
  let start = new Date(now);

  switch (period) {
    case 'WEEK':
      start = startOfDay(new Date(end));
      start.setDate(start.getDate() - 7);
      break;
    case 'MONTH':
      start = startOfDay(new Date(end));
      start.setMonth(start.getMonth() - 1);
      break;
    case 'YTD':
      start = startOfDay(new Date(end.getFullYear(), 0, 1));
      break;
    default:
      return {
        startDate: formatDateTimeForApi(new Date(now.getTime() - PERIOD_DAY_MS)),
        endDate: formatDateTimeForApi(now),
      };
  }

  return {
    startDate: formatDateTimeForApi(start),
    endDate: formatDateTimeForApi(end),
  };
};

export const normalizeStatisticsResponse = (stats) => {
  const payload = Array.isArray(stats) ? stats[0] : stats;

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const statistics = Array.isArray(payload.statistics) ? payload.statistics : [];
  const summary = statistics[0] || {};
  const rawData = Array.isArray(payload.raw_data) ? payload.raw_data : [];
  const chartData = Array.isArray(payload.chart_data) ? payload.chart_data : [];
  const completedTrades = Number(
    payload.closed_trades ?? summary.closedTrades ?? (chartData.length > 0 ? chartData.length : 0),
  );
  const hasMeaningfulData =
    statistics.length > 0 ||
    rawData.length > 0 ||
    chartData.length > 0 ||
    payload.has_closed_trades === false ||
    Boolean(payload.message);
  const hasClosedTrades = Boolean(
    payload.has_closed_trades ?? summary.closedTrades ?? chartData.length > 0,
  );

  if (!hasMeaningfulData) {
    return null;
  }

  if (!hasClosedTrades) {
    return {
      hasClosedTrades: false,
      message: payload.message || 'No closed trades for the selected period.',
      completedTrades: 0,
      chartData: [],
    };
  }

  const winRate =
    summary.winRate === undefined || summary.winRate === null
      ? completedTrades > 0
        ? (Number(payload.winning_trades ?? summary.winningTrades ?? 0) / completedTrades) * 100
        : 0
      : Number(summary.winRate);

  return {
    hasClosedTrades: true,
    message: payload.message || null,
    avgTradeTime: payload.avg_trade_time || '0m',
    totalPnl: Number(summary.totalPnl ?? payload.totalPnl ?? 0),
    winRate,
    winLossRatio:
      summary.profit_loss_ratio === undefined || summary.profit_loss_ratio === null
        ? null
        : Number(summary.profit_loss_ratio),
    winLossRatioDisplay: summary.profit_loss_ratio_display || null,
    profitFactor:
      summary.ProfLoss === undefined || summary.ProfLoss === null
        ? null
        : Number(summary.ProfLoss),
    profitFactorDisplay: summary.profit_factor_display || null,
    completedTrades,
    periodStartEquity: Number(payload.period_start_equity ?? 0),
    periodEndEquity: Number(payload.period_end_equity ?? payload.totalPnl ?? 0),
    chartData,
  };
};

export const formatAverageTimeInTrade = (avgTradeTime, completedTrades) => {
  if (completedTrades > 0 && (!avgTradeTime || avgTradeTime === '0m' || avgTradeTime === '0')) {
    return '<1m';
  }

  return avgTradeTime || '0m';
};

export const formatSymbolSelection = (symbols = []) => {
  if (!symbols.length) {
    return 'No symbols selected';
  }

  if (symbols.length === 1) {
    return `Symbol: ${symbols[0]}`;
  }

  const visibleSymbols = symbols.slice(0, 3).join(', ');
  const hiddenCount = symbols.length - 3;

  return hiddenCount > 0
    ? `Symbols: ${visibleSymbols} +${hiddenCount}`
    : `Symbols: ${visibleSymbols}`;
};

export const buildProfileChartSeries = (chartData = []) => {
  if (!Array.isArray(chartData) || chartData.length === 0) {
    return [];
  }

  let runningTotal = 0;
  const tradePoints = chartData.map((point, index) => {
    runningTotal += Number(point.pnl) || 0;
    return {
      ...point,
      trade_number: index + 1,
      period_cumulative_pnl: runningTotal,
    };
  });

  const tradeLabels = buildTradeLabels(tradePoints);
  const labeledTrades = tradePoints.map((point, index) => ({
    ...point,
    trade_label: tradeLabels[index],
  }));

  return [
    {
      timestamp: null,
      pnl: 0,
      period_cumulative_pnl: 0,
      trade_number: 0,
      trade_label: 'Start',
      is_anchor: true,
    },
    ...labeledTrades,
  ];
};

// Backwards-compatible alias for older imports/tests.
export const buildChartSeries = buildProfileChartSeries;
